/**
 * Состояние чата — перенос класса `ChatValue` из `chat_controller.dart`.
 *
 * Вынесено в отдельный файл: в Dart он лежал рядом с контроллером, но там
 * один файл на 918 строк, а здесь действует потолок в 800.
 *
 * Класс, а не простой объект: во Flutter-виджетах повсюду встречаются
 * `chatValue.busy`, `chatValue.firstQuestion`, `chatValue.showChat` —
 * с геттерами эти места переносятся дословно. Поля только для чтения,
 * изменение — всегда новый объект через [ChatValue.copyWith].
 */

import type { AppLanguage } from '@/domain/models/appLanguage';
import type { PersonModel } from '@/domain/models/personModel';
import { resolveNullable, type Nullable } from '@/utils/nullable';

import type { ChatMessage } from './chatMessage';

/** Сколько сообщений должно накопиться, чтобы показать ленту (см. `showChat`). */
const CHAT_VISIBLE_MESSAGE_COUNT = 2;

/** Аргументы конструктора [ChatValue] — значения по умолчанию те же. */
export interface ChatValueInit {
  readonly isRecording?: boolean;
  readonly animationConnectionId?: string | null;
  readonly translating?: boolean;
  readonly checkingAccess?: boolean;
  readonly userHolding?: boolean;
  readonly keyboardMode?: boolean;
  readonly preparingFile?: boolean;
  readonly preparingDirectory?: boolean;
  readonly connectionId?: string | null;
  readonly speeking?: boolean;
  readonly person: PersonModel;
  readonly language: AppLanguage;
  readonly promptInfos?: readonly Record<string, unknown>[];
  readonly messages?: readonly ChatMessage[];
  readonly personAnimating?: boolean;
  readonly complete?: boolean;
  readonly personAnimationComplete?: boolean;
}

/**
 * Патч [ChatValue.copyWith].
 *
 * `connectionId` и `animationConnectionId` обёрнуты в `Nullable` — это тот
 * же sentinel, что в Dart: поле без обёртки не трогается, `new Nullable(null)`
 * его обнуляет (именно так закрываются соединения).
 */
export interface ChatValuePatch {
  readonly preparingFile?: boolean;
  readonly isRecording?: boolean;
  readonly translating?: boolean;
  readonly checkingAccess?: boolean;
  readonly userHolding?: boolean;
  readonly keyboardMode?: boolean;
  readonly messages?: readonly ChatMessage[];
  readonly language?: AppLanguage;
  readonly connectionId?: Nullable<string | null>;
  readonly animationConnectionId?: Nullable<string | null>;
  readonly preparingDirectory?: boolean;
  readonly person?: PersonModel;
  readonly speeking?: boolean;
  readonly personAnimating?: boolean;
  readonly promptInfos?: readonly Record<string, unknown>[];
  readonly complete?: boolean;
  readonly personAnimationComplete?: boolean;
}

export class ChatValue {
  constructor(init: ChatValueInit) {
    this.personAnimating = init.personAnimating ?? false;
    this.personAnimationComplete = init.personAnimationComplete ?? false;
    this.connectionId = init.connectionId ?? null;
    this.animationConnectionId = init.animationConnectionId ?? null;
    this.checkingAccess = init.checkingAccess ?? false;
    this.speeking = init.speeking ?? false;
    this.isRecording = init.isRecording ?? false;
    this.translating = init.translating ?? false;
    this.userHolding = init.userHolding ?? false;
    this.keyboardMode = init.keyboardMode ?? false;
    this.preparingDirectory = init.preparingDirectory ?? false;
    this.preparingFile = init.preparingFile ?? false;
    this.language = init.language;
    this.person = init.person;
    this.complete = init.complete ?? false;
    this.messages = init.messages ?? [];
    this.promptInfos = init.promptInfos ?? [];
  }

  readonly personAnimating: boolean;
  readonly personAnimationComplete: boolean;

  /** Идентификатор живого SSE-ответа; `null` — ответа сейчас не ждём. */
  readonly connectionId: string | null;

  /** Идентификатор живого SSE-потока анимации аватара. */
  readonly animationConnectionId: string | null;

  readonly checkingAccess: boolean;
  readonly speeking: boolean;
  readonly isRecording: boolean;
  readonly translating: boolean;
  readonly userHolding: boolean;
  readonly keyboardMode: boolean;
  readonly preparingDirectory: boolean;
  readonly preparingFile: boolean;
  readonly language: AppLanguage;
  readonly person: PersonModel;
  readonly complete: boolean;

  /** Лента сообщений: новые в начале (`addMessage` кладёт их первыми). */
  readonly messages: readonly ChatMessage[];

  readonly promptInfos: readonly Record<string, unknown>[];

  toString(): string {
    return `ChatValue(
      checkingAccess: ${this.checkingAccess}, isRecording: ${this.isRecording}, translating: ${this.translating}, userHolding: ${this.userHolding}, preparingDirectory: ${this.preparingDirectory}, preparingFile ${this.preparingFile}
    )`;
  }

  /**
   * Последнее сообщение ленты — самый первый вопрос диалога: новые лежат
   * в начале, значит первый — в конце.
   *
   * В Dart тут была защитная копия `[...messages]`: список там изменяемый.
   * Здесь массив `readonly`, копировать нечего.
   */
  get firstQuestion(): ChatMessage | null {
    const firstQuestionIndex = this.messages.length - 1;
    return firstQuestionIndex >= 0 ? this.messages[firstQuestionIndex] : null;
  }

  /** Ответ на первый вопрос — предпоследнее сообщение ленты. */
  get firstAnswer(): ChatMessage | null {
    const firstQuestionIndex = this.messages.length - 1;
    const firstAnswerIndex = firstQuestionIndex - 1;
    return firstAnswerIndex >= 0 ? this.messages[firstAnswerIndex] : null;
  }

  /** Занято: пока true, отсчёт бездействия стоит. */
  get busy(): boolean {
    return (
      this.fetchingResponse ||
      this.checkingAccess ||
      this.isRecording ||
      this.translating ||
      this.userHolding
    );
  }

  get fetchingResponse(): boolean {
    return this.connectionId !== null;
  }

  /**
   * Микрофон ещё готовится: на этой стадии отпускание кнопки отменяет
   * дубль целиком (см. `_stopRecordAudio`).
   */
  get preparingBeforeRecord(): boolean {
    return this.checkingAccess || this.preparingDirectory;
  }

  get showChat(): boolean {
    return (
      (this.messages.length === CHAT_VISIBLE_MESSAGE_COUNT && !this.fetchingResponse) ||
      this.messages.length > CHAT_VISIBLE_MESSAGE_COUNT
    );
  }

  copyWith(patch: ChatValuePatch): ChatValue {
    return new ChatValue({
      personAnimationComplete: patch.personAnimationComplete ?? this.personAnimationComplete,
      complete: patch.complete ?? this.complete,
      animationConnectionId: resolveNullable(
        patch.animationConnectionId,
        this.animationConnectionId,
      ),
      personAnimating: patch.personAnimating ?? this.personAnimating,
      speeking: patch.speeking ?? this.speeking,
      keyboardMode: patch.keyboardMode ?? this.keyboardMode,
      promptInfos: patch.promptInfos ?? this.promptInfos,
      person: patch.person ?? this.person,
      preparingDirectory: patch.preparingDirectory ?? this.preparingDirectory,
      preparingFile: patch.preparingFile ?? this.preparingFile,
      checkingAccess: patch.checkingAccess ?? this.checkingAccess,
      userHolding: patch.userHolding ?? this.userHolding,
      language: patch.language ?? this.language,
      messages: patch.messages ?? this.messages,
      translating: patch.translating ?? this.translating,
      isRecording: patch.isRecording ?? this.isRecording,
      connectionId: resolveNullable(patch.connectionId, this.connectionId),
    });
  }
}

/**
 * Поэлементное сравнение списков.
 *
 * В Dart тут был `DeepCollectionEquality().hash(...)`, но сравнивать вглубь
 * нечего: элементы неизменяемы и пересоздаются только вместе с изменением —
 * значит совпадение ссылок означает совпадение содержимого.
 */
function sameItems(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((item, index) => Object.is(item, b[index]));
}

/**
 * `List<Object?> get props` из Equatable — в том же порядке полей.
 *
 * Контроллеру он не нужен: `CustomValueNotifier.setValue` сравнивает
 * значения через `Object.is`, а каждое изменение создаёт новый объект,
 * так что сравнения по ссылке достаточно; единственное сравнение,
 * от которого зависит поведение, — `current.complete !== previous.complete`
 * в `onStateChanged`, и оно идёт по полю. Функция оставлена для тестов и
 * для мест, где важно отличить «состояние пересобрали» от «состояние
 * изменилось».
 *
 * Отличие от Dart: `person` сравнивается по ссылке — `PersonModel` в порте
 * это простой объект, а не freezed-класс со значимым равенством.
 */
export function chatValueEquals(a: ChatValue, b: ChatValue): boolean {
  return (
    a.preparingDirectory === b.preparingDirectory &&
    a.preparingFile === b.preparingFile &&
    a.isRecording === b.isRecording &&
    a.translating === b.translating &&
    a.connectionId === b.connectionId &&
    a.checkingAccess === b.checkingAccess &&
    a.userHolding === b.userHolding &&
    a.keyboardMode === b.keyboardMode &&
    sameItems(a.messages, b.messages) &&
    sameItems(a.promptInfos, b.promptInfos) &&
    a.speeking === b.speeking &&
    a.language === b.language &&
    a.person === b.person &&
    a.personAnimating === b.personAnimating &&
    a.personAnimationComplete === b.personAnimationComplete &&
    a.animationConnectionId === b.animationConnectionId &&
    a.complete === b.complete
  );
}
